import React, { useState, useEffect } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { LoaderCircle } from 'lucide-react';
import { CostPercentageTimeData } from '../../../utils/chartsUtils';
import { useChartTheme } from '../../../utils/chartTheme';

interface InslagOverTimeChartProps {
  data: CostPercentageTimeData[];
  loading?: boolean;
}

export const InslagOverTimeChart: React.FC<InslagOverTimeChartProps> = ({ data, loading }) => {
  const { t } = useTranslation();
  const theme = useChartTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (data.length === 0) {
    return (
      <div className="bg-white dark:bg-card-dark p-4 rounded-[12px] shadow-[0px_4px_30px_0px_#4D546408] dark:shadow-[0px_4px_30px_0px_#00000040]">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <LoaderCircle className="h-8 w-8 animate-spin text-textPrimary dark:text-textPrimary-dark" />
          </div>
        ) : (
          <p className="text-[#475467] dark:text-textSecondary-dark text-center py-8">{t('charts.noData')}</p>
        )}
      </div>
    );
  }

  const gradientId = isOpen ? 'inslagGradientModal' : 'inslagGradient';

  const ChartContent = () => (
    <ResponsiveContainer width="100%" height="100%">
      <AreaChart
        data={data}
        margin={{ top: 5, right: isMobile ? 0 : 5, left: isMobile ? -20 : -20, bottom: 0 }}
      >
        <defs>
          <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#3DCB9A" stopOpacity={0.1} />
            <stop offset="100%" stopColor="#3DCB9A" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid
          strokeDasharray="3 3"
          vertical={false}
          stroke={theme.gridLine}
        />

        <XAxis
          dataKey="label"
          type="category"
          axisLine={false}
          tickLine={false}
          tick={{ fill: theme.axisTick, fontSize: isMobile ? 10 : 12 }}
          dy={10}
        />

        <YAxis
          axisLine={false}
          tickLine={false}
          tick={{ fill: theme.axisTick, fontSize: isMobile ? 10 : 12 }}
          domain={[0, 100]}
          tickFormatter={(value: number) => `${value}%`}
        />

        <Tooltip
          contentStyle={{
            borderRadius: "8px",
            padding: "6px 8px",
            border: `1px solid ${theme.tooltipBorder}`,
            boxShadow: "0px 12px 30px 0px #CFCFDC1A",
            fontSize: "12px",
            backgroundColor: theme.tooltipBg,
          }}
          itemStyle={{ fontSize: "14px", lineHeight: '20px' }}
          labelStyle={{ fontSize: "14px", fontWeight: "medium", color: theme.tooltipText, lineHeight: "20px" }}
          formatter={(value: number) => [`${value.toFixed(1)}%`, t('charts.costPercentageShort')]}
        />

        <ReferenceLine
          y={50}
          stroke="#fb923c"
          strokeDasharray="4 4"
          label={{ position: "right", fill: "#EF6820", fontSize: isMobile ? 8 : 10 }}
        />

        <ReferenceLine
          y={32}
          stroke="#2E90FA"
          strokeDasharray="4 4"
        />

        <Area
          type="monotone"
          dataKey="costPercentage"
          stroke="#18B081"
          strokeWidth={isMobile ? 1.5 : 2}
          fill={`url(#${gradientId})`}
          dot={false}
          activeDot={{
            r: isMobile ? 4 : 6,
            fill: "#18B081",
            stroke: "#fff",
            strokeWidth: 2,
          }}
        />
      </AreaChart>
    </ResponsiveContainer>
  );

  return (
    <div className="w-full bg-white dark:bg-card-dark rounded-[12px] p-5 relative shadow-[0px_4px_30px_0px_#4D546408] dark:shadow-[0px_4px_30px_0px_#00000040]">
      {/* Header */}
      <div className='mb-4 sm:mb-8 flex items-center justify-between w-full'>
        <div>
          <h1 className='text-[#101828] dark:text-textPrimary-dark text-[20px] leading-[30px] font-bold mb-1'>{t('charts.costPercentageOverTime')}</h1>
          <p className='text-[#475467] dark:text-textSecondary-dark text-[14px] leading-[21px] font-normal'>{t('charts.costPercentageOverTimeDescription')}</p>
        </div>
        <button
          className='border border-[#D0D5DD] dark:border-borderPrimary-dark rounded-[10px] h-10 pl-2 pr-3 hidden md:flex items-center gap-1 hover:bg-gray-100 dark:hover:bg-backgroundDarker-dark transition-all duration-300'
          onClick={() => setIsOpen(true)}
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[#475467] dark:text-textSecondary-dark">
            <path d="M5.83317 14.1667H8.33317C8.56928 14.1667 8.7672 14.2466 8.92692 14.4063C9.08664 14.5661 9.1665 14.764 9.1665 15.0001C9.1665 15.2362 9.08664 15.4341 8.92692 15.5938C8.7672 15.7536 8.56928 15.8334 8.33317 15.8334H4.99984C4.76373 15.8334 4.56581 15.7536 4.40609 15.5938C4.24637 15.4341 4.1665 15.2362 4.1665 15.0001V11.6667C4.1665 11.4306 4.24637 11.2327 4.40609 11.073C4.56581 10.9133 4.76373 10.8334 4.99984 10.8334C5.23595 10.8334 5.43387 10.9133 5.59359 11.073C5.75331 11.2327 5.83317 11.4306 5.83317 11.6667V14.1667ZM14.1665 5.83341H11.6665C11.4304 5.83341 11.2325 5.75355 11.0728 5.59383C10.913 5.43411 10.8332 5.23619 10.8332 5.00008C10.8332 4.76397 10.913 4.56605 11.0728 4.40633C11.2325 4.24661 11.4304 4.16675 11.6665 4.16675H14.9998C15.2359 4.16675 15.4339 4.24661 15.5936 4.40633C15.7533 4.56605 15.8332 4.76397 15.8332 5.00008V8.33342C15.8332 8.56953 15.7533 8.76744 15.5936 8.92717C15.4339 9.08689 15.2359 9.16675 14.9998 9.16675C14.7637 9.16675 14.5658 9.08689 14.4061 8.92717C14.2464 8.76744 14.1665 8.56953 14.1665 8.33342V5.83341Z" fill="currentColor" />
          </svg>
          <span className='text-[#667085] dark:text-textQuaternary-dark text-[14px] leading-5 font-semibold'>Expand</span>
        </button>
      </div>

      <div className="h-[229px] w-full">
        <ChartContent />
      </div>

      {/* Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#16192466] dark:bg-[#16192466]" onClick={() => setIsOpen(false)}>
          <div className="bg-white dark:bg-card-dark rounded-xl w-full max-w-[850px] p-5 relative" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <div>
                <h1 className='text-[#101828] dark:text-textPrimary-dark text-[20px] leading-[30px] font-bold mb-1'>{t('charts.costPercentageOverTime')}</h1>
                <p className='text-[#475467] dark:text-textSecondary-dark text-[14px] leading-[21px] font-normal'>{t('charts.costPercentageOverTimeDescription')}</p>
              </div>
              <button
                onClick={() => setIsOpen(false)}
                className="border border-[#D0D5DD] dark:border-borderPrimary-dark rounded-[10px] h-10 pl-2 pr-3 flex items-center gap-1 hover:bg-gray-100 dark:hover:bg-backgroundDarker-dark transition-all duration-300"
              >
                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-[#475467] dark:text-textSecondary-dark">
                  <path d="M7.49984 12.5001H4.99984C4.76373 12.5001 4.56581 12.4202 4.40609 12.2605C4.24637 12.1008 4.1665 11.9029 4.1665 11.6667C4.1665 11.4306 4.24637 11.2327 4.40609 11.073C4.56581 10.9133 4.76373 10.8334 4.99984 10.8334H8.33317C8.56928 10.8334 8.7672 10.9133 8.92692 11.073C9.08664 11.2327 9.1665 11.4306 9.1665 11.6667V15.0001C9.1665 15.2362 9.08664 15.4341 8.92692 15.5938C8.7672 15.7536 8.56928 15.8334 8.33317 15.8334C8.09706 15.8334 7.89914 15.7536 7.73942 15.5938C7.5797 15.4341 7.49984 15.2362 7.49984 15.0001V12.5001ZM12.4998 7.50008H14.9998C15.2359 7.50008 15.4339 7.57994 15.5936 7.73966C15.7533 7.89939 15.8332 8.0973 15.8332 8.33342C15.8332 8.56953 15.7533 8.76744 15.5936 8.92717C15.4339 9.08689 15.2359 9.16675 14.9998 9.16675H11.6665C11.4304 9.16675 11.2325 9.08689 11.0728 8.92717C10.913 8.76744 10.8332 8.56953 10.8332 8.33342V5.00008C10.8332 4.76397 10.913 4.56605 11.0728 4.40633C11.2325 4.24661 11.4304 4.16675 11.6665 4.16675C11.9026 4.16675 12.1005 4.24661 12.2603 4.40633C12.42 4.56605 12.4998 4.76397 12.4998 5.00008V7.50008Z" fill="currentColor" />
                </svg>
                <span className="text-[#667085] dark:text-textQuaternary-dark text-[14px] leading-5 font-semibold">Collapse</span>
              </button>
            </div>
            <div className="w-full h-[350px]">
              <ChartContent />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
